import { useEffect, useMemo, useRef, useState } from "react";
import SVG from "../../../Components/SVG";
import {
  getTradeIconUrls,
  isLocalTradeIcon,
  normalizeSymbolCode,
} from "../tradeUtils";

export function CoinIcon({ code, className = "size-4" }) {
  const normalized = normalizeSymbolCode(code);
  const iconUrls = useMemo(() => getTradeIconUrls(normalized), [normalized]);
  const [urlIndex, setUrlIndex] = useState(0);

  useEffect(() => {
    setUrlIndex(0);
  }, [normalized]);

  if (!normalized) {
    return (
      <span
        className={`${className} inline-flex items-center justify-center rounded-full bg-zi-bg1-light dark:bg-zi-bg1-dark text-[10px] uppercase`}
      >
        ?
      </span>
    );
  }

  if (isLocalTradeIcon(normalized)) {
    return <SVG name={normalized} classname={className} />;
  }

  if (urlIndex >= iconUrls.length) {
    return (
      <span
        className={`${className} inline-flex items-center justify-center rounded-full bg-zi-bg1-light dark:bg-zi-bg1-dark text-[10px] uppercase`}
      >
        {normalized[0]}
      </span>
    );
  }

  return (
    <img
      src={iconUrls[urlIndex]}
      alt={`${normalized.toUpperCase()} icon`}
      className={`${className} object-contain rounded-full`}
      loading="lazy"
      onError={() => setUrlIndex((prev) => prev + 1)}
    />
  );
}

function BackendSvgIcon({ svgCode, alt, className = "size-4" }) {
  const trimmedSvg = String(svgCode ?? "").trim();
  const svgSrc = useMemo(() => {
    if (!trimmedSvg) return "";
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(trimmedSvg)}`;
  }, [trimmedSvg]);

  if (!svgSrc) return null;

  return (
    <img
      src={svgSrc}
      alt={alt}
      className={`${className} object-contain`}
      loading="lazy"
    />
  );
}

function SymbolIcon({
  svgCode,
  fallbackCode,
  alt,
  className = "size-4",
}) {
  if (String(svgCode ?? "").trim()) {
    return <BackendSvgIcon svgCode={svgCode} alt={alt} className={className} />;
  }

  if (fallbackCode) {
    return <CoinIcon code={fallbackCode} className={className} />;
  }

  return null;
}

export default function SymbolSelect({
  options = [],
  value, // optional controlled value
  defaultValue, // optional uncontrolled initial value
  onChange, // optional callback (val) => {}
  name = "symbol",
  label = "نماد",
  setSymbol, // optional legacy callback for uncontrolled usage
  className = "",
}) {
  const normalizeValue = (rawValue) => {
    const normalized = normalizeSymbolCode(rawValue);
    return normalized || String(rawValue ?? "");
  };

  const [internalValue, setInternalValue] = useState(
    normalizeValue(value ?? defaultValue ?? (options[0] ? options[0].value : "")),
  );
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const containerRef = useRef(null);
  const menuRef = useRef(null);
  const selectedOptionRef = useRef(null);
  const searchRef = useRef(null);

  // Controlled vs uncontrolled
  const selectedValue = normalizeValue(value ?? internalValue);
  const selectedOption =
    options.find((o) => normalizeValue(o.value) === selectedValue) ||
    options[0] ||
    null;

  const filteredOptions = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return options;

    return options.filter((opt) => {
      const labelText = String(opt?.label ?? "").toLowerCase();
      const valueText = String(opt?.value ?? "").toLowerCase();
      const fullnameText = String(
        opt?.fullname ?? opt?.full_name ?? opt?.fullName ?? "",
      ).toLowerCase();
      return (
        labelText.includes(q) ||
        valueText.includes(q) ||
        fullnameText.includes(q)
      );
    });
  }, [options, searchTerm]);

  const handleSelect = (val) => {
    const normalized = normalizeValue(val);

    if (onChange) {
      onChange(normalized);
    } else {
      setInternalValue(normalized);
    }

    setIsOpen(false);
    setSearchTerm("");
  };

  // Close on outside click + Esc
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  useEffect(() => {
    if (onChange) return;
    if (!setSymbol) return;
    if (!selectedOption) return;

    const normalized = normalizeValue(selectedOption.value);
    setSymbol(normalized);
  }, [onChange, selectedOption, setSymbol]);

  useEffect(() => {
    if (value === undefined) return;
    setInternalValue(normalizeValue(value));
  }, [value]);

  useEffect(() => {
    if (!options.length) return;

    const hasSelected = options.some(
      (opt) => normalizeValue(opt.value) === selectedValue,
    );

    if (!hasSelected) {
      const fallback = normalizeValue(options[0].value);
      if (onChange) {
        onChange(fallback);
      } else {
        setInternalValue(fallback);
      }
    }
  }, [options, onChange, selectedValue]);

  useEffect(() => {
    if (!isOpen) return;
    requestAnimationFrame(() => searchRef.current?.focus());
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const frame = requestAnimationFrame(() => {
      const menu = menuRef.current;
      const option = selectedOptionRef.current;
      if (!menu || !option) return;

      const centeredScrollTop =
        option.offsetTop - (menu.clientHeight - option.offsetHeight) / 2;
      const maxScrollTop = Math.max(0, menu.scrollHeight - menu.clientHeight);
      menu.scrollTop = Math.min(Math.max(0, centeredScrollTop), maxScrollTop);
    });

    return () => cancelAnimationFrame(frame);
  }, [isOpen, selectedValue]);

  return (
    <div ref={containerRef} className={`relative w-full ${className}`}>
      {/* Hidden real select for forms */}
      {name && (
        <select className="hidden" name={name} value={selectedValue} readOnly>
          {options.map((o) => (
            <option key={o.value} value={normalizeValue(o.value)}>
              {o.value}
            </option>
          ))}
        </select>
      )}

      {/* Main pill */}
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="
          relative w-full flex items-center justify-end xl:justify-between gap-1
          rounded-[10px] px-3 py-1.5
          bg-zi-bg1-light dark:bg-zi-inputbg-dark
          text-zi-text-light dark:text-zi-text-dark
          text-base
        "
      >
        {/* Right label (جفت ارز) */}
        <span className="text-sm hidden xl:inline text-nowrap text-zi-text-light/50 dark:text-zi-text-dark/50">
          {label}
        </span>

        <div className="flex items-center gap-1 sm:gap-2.5">
          {/* Center: selected symbol + icons */}
          {selectedOption && (
            <div className="flex items-center gap-1 sm:gap-2.5">
              {/* pair text should be LTR */}
              <span dir="ltr" className="">
                {selectedOption.label}
              </span>

              <div className="flex items-center gap-2">
                {(selectedOption.svg || selectedOption.icons?.[0]) && (
                  <span className="">
                    <SymbolIcon
                      svgCode={selectedOption.svg}
                      fallbackCode={selectedOption.icons?.[0]}
                      alt={`${selectedOption.label} icon`}
                      className="size-4"
                    />
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Left arrow */}
          <span className="flex items-center justify-center">
            <SVG
              name="select"
              classname={`size-3 stroke-zi-text-light dark:stroke-zi-text-dark ${
                isOpen ? "rotate-180" : ""
              }`}
            />
          </span>
        </div>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div
          ref={menuRef}
          className="
            absolute inset-x-0 mt-2 z-50
            max-h-64 overflow-auto
            rounded-[10px] border border-zi-border-light/60 dark:border-zi-border-light/10
            bg-zi-bg2-light dark:bg-zi-bg2-dark
            shadow-lg
          "
        >
          <div className="sticky top-0 z-10 bg-zi-bg2-light dark:bg-zi-bg2-dark border-b border-zi-border-light/60 dark:border-b-0 mb-2">
            <div className="p-2">
              <input
                ref={searchRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="جستجوی نماد..."
                className="w-full rounded-lg px-2.5 py-1.5 text-sm outline-none border border-zi-border-light/60 dark:border-transparent bg-zi-bg1-light dark:bg-zi-inputbg-dark text-zi-text-light dark:text-zi-text-dark"
              />
            </div>
            <div className="card-divider hidden dark:block w-full h-px bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.09)_20%,rgba(255,255,255,0.22)_50%,rgba(255,255,255,0.09)_80%,transparent)]" />
          </div>

          {filteredOptions.map((opt) => {
            const normalizedOptionValue = normalizeValue(opt.value);
            const isActive = normalizedOptionValue === selectedValue;
            return (
              <button
                key={opt.value}
                ref={isActive ? selectedOptionRef : null}
                type="button"
                onClick={() => handleSelect(normalizedOptionValue)}
                className={`
                  w-full px-3 py-2 flex items-center gap-2.5 text-base [direction:ltr] text-zi-text-light dark:text-zi-text-dark
                  hocus:bg-zi-bg1-light/50 dark:hocus:bg-white/[0.03]
                  ${isActive ? "bg-zi-bg1-light/50 dark:!bg-neutral-800" : ""}
                `}
              >
                <div className="flex items-center gap-2.5">
                  {/* pair text */}
                  <span dir="ltr" className="">
                    {opt.label}
                  </span>

                  {/* icons */}
                  <div className="flex items-center gap-2">
                    {(opt.svg || opt.icons?.[0]) && (
                      <span className="">
                        <SymbolIcon
                          svgCode={opt.svg}
                          fallbackCode={opt.icons?.[0]}
                          alt={`${opt.label} icon`}
                          className="size-4"
                        />
                      </span>
                    )}
                  </div>
                </div>

                {/* Checkmark for selected */}
                {isActive && (
                  <SVG
                    name="check"
                    classname="size-4 stroke-zi-text-light dark:stroke-zi-text-dark"
                  />
                )}
              </button>
            );
          })}

          {filteredOptions.length === 0 && (
            <div className="px-3 py-4 text-center text-sm text-zi-text-light/60 dark:text-zi-text-dark/60">
              نتیجه‌ای یافت نشد
            </div>
          )}
        </div>
      )}
    </div>
  );
}
